/* eslint-disable @next/next/no-img-element */
import React from "react";
import { useQuery } from "react-query";
import { useRouter } from "next/router";
import { XIcon } from "@heroicons/react/outline";
import { SECTIONS_TYPES } from "@gonoodle/gn-universe-analytics-schema/src/constants";

import { useProfile } from "../../hooks";
import { useLogEvent } from "../../contexts/Analytics";
import {
  getSourcePageAndSourcePageTypeFromReferrer,
  getUtmParamsFromQueryParams,
} from "../../utils/analytics";
import api from "../../api";
import { QUERY_KEYS } from "../../constants";
import Link from "../Link";

const client = api();

function OnSiteMessages(_, ref) {
  const [messageVisible, setMessageVisible] = React.useState(true);
  const router = useRouter();
  const { profile } = useProfile();
  const { data: messages = null, isLoading: isFetchingMessages } = useQuery(
    QUERY_KEYS.ON_SITE_MESSAGES,
    () => client.getOnSiteMessages(),
    {
      staleTime: Infinity,
    },
  );

  const analyticsEventProperties = {
    sourceElement: SECTIONS_TYPES.ANNOUNCEMENT_BANNER,
    sourceName: messages?.message,
    title: messages?.internalTitle?.toLowerCase(),
    ...getSourcePageAndSourcePageTypeFromReferrer(
      router.pathname,
      router.query,
    ),
    ...getUtmParamsFromQueryParams(router.query),
    gradeId: profile?.grade.id,
    size: profile?.size,
    schoolId: profile?.school?.id,
    url: messages?.destinationUrl,
  };

  useLogEvent({
    event: "Announcement banner shown",
    properties: {
      ...analyticsEventProperties,
    },
    options: {
      enabled: !!messages,
    },
  });

  if (isFetchingMessages) {
    return null;
  }

  const closeBanner = () => {
    setMessageVisible(false);
  };

  if (messages) {
    const {
      backgroundColor,
      ctaPhrase,
      destinationUrl,
      logo,
      logoAltText,
      message,
      textTheme,
    } = messages;
    const textColor = textTheme === "light" ? "text-white" : "text-gray-900";

    return (
      messageVisible && (
        <section
          className={`gn-text-sm p-sm md:py-sm md:px-md flex items-center justify-end ${textColor}`}
          style={{ backgroundColor: `#${backgroundColor}` }}
          ref={ref}
        >
          <span className="text-left lg:text-right md:items-center md:pr-md flex flex-col md:flex-row items-start justify-center flex-1">
            <span>
              {message}{" "}
              {ctaPhrase && destinationUrl && (
                <Link
                  to={destinationUrl}
                  className="underline"
                  events={[
                    {
                      event: "Announcement banner clicked",
                      properties: {
                        ...analyticsEventProperties,
                      },
                    },
                  ]}
                  queryParams={{
                    referrer: {
                      sourceElement: SECTIONS_TYPES.ANNOUNCEMENT_BANNER,
                      sourceName: ctaPhrase,
                      ...getSourcePageAndSourcePageTypeFromReferrer(
                        router.pathname,
                        router.query,
                      ),
                    },
                  }}
                >
                  {ctaPhrase}
                </Link>
              )}
            </span>
            {logo && (
              <div className="flex items-center mt-2 md:mt-0 shrink-0">
                <span className="opacity-30 hidden md:flex mx-6 h-[40px] border-purple-100 border-sm  border-r-0" />
                <span className="text-xxs font-bold">Powered by</span>
                <img
                  alt={logoAltText || ""}
                  className="ml-xs h-[40px]"
                  height="40"
                  src={logo}
                />
              </div>
            )}
          </span>
          <button onClick={closeBanner}>
            <span className="sr-only">Close banner</span>
            <XIcon className="w-6 h-6 ml-4" />
          </button>
        </section>
      )
    );
  }
}

export default React.forwardRef(OnSiteMessages);
